'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import Icon from '@mdi/react'
import { mdiCheckCircleOutline, mdiChevronRight } from '@mdi/js'
import React from 'react'

// lib
import Box from '@components/kit/box'
import { BoxLink } from '@components/kit/link'
import Text from '@components/kit/text'
import { useNeedsAttentionStore } from '@store/NeedsAttention'
import { colors } from '@theme'

/* -----------------------------------------------------------------------------
 * MemberQueueBar
 *
 * Where the member sits in the professional's needs-attention queue, and the
 * member to open next: the same one the chat's queue tab offers.
 * -------------------------------------------------------------------------- */

type MemberQueueBarProps = {
  userId: number
}

function MemberQueueBar({ userId }: MemberQueueBarProps) {
  const nextMember = useNeedsAttentionStore(state => state.nextMember)

  // nothing until the queue was read for this member
  if (nextMember?.forUserId !== userId) {
    return null
  }

  const {
    isPartial,
    isSkipped,
    skippedCount,
    total,
    userId: nextUserId
  } = nextMember
  const status = nextUserId
    ? isSkipped
      ? `End of your queue · ${skippedCount} skipped`
      : `${total}${isPartial ? '+' : ''} in your queue`
    : total > 0
    ? 'Last one in your queue'
    : 'Your queue is clear'

  return (
    <Box
      alignItems='center'
      borderBottomWidth={1}
      borderColor='proBorder'
      flexDirection='row'
      justifyContent='space-between'
      mb='16'
      pb='08'
    >
      <Box alignItems='center' flexDirection='row'>
        {!nextUserId && total === 0 ? (
          <Box mr='04'>
            <Icon
              color={colors.proTeal}
              path={mdiCheckCircleOutline}
              size='16px'
            />
          </Box>
        ) : null}
        <Text font='pro.text.12.regular' textColor='proTextMuted'>
          {status}
        </Text>
      </Box>
      {nextUserId ? (
        <BoxLink
          alignItems='center'
          flexDirection='row'
          path={`/chat/${nextUserId}`}
        >
          <Text font='pro.text.12.semibold' textColor='proAccent'>
            {isSkipped ? `Back to ${nextUserId}` : `Next: ${nextUserId}`}
          </Text>
          <Icon color={colors.proAccent} path={mdiChevronRight} size='16px' />
        </BoxLink>
      ) : null}
    </Box>
  )
}

export default MemberQueueBar
